<div class="page">
  @if (session(); as sess) {
    <header class="brijjsports-page-head">
      <p class="brijjsports-page-kicker">Coach · Basketball</p>
      <h1 class="brijjsports-page-title">{{ sess.name }}</h1>
      @if (game(); as g) {
        <p class="brijjsports-page-sub">vs {{ g.opponent }} · {{ g.quarterCount === 4 ? '4 quarters' : '2 halves' }}</p>
      }
    </header>

    @if (game(); as g) {
      <section class="bb-scoreboard brijjsports-card" aria-label="Live scoreboard">
        <div class="bb-side bb-side--home">
          <span class="bb-side__label">Home</span>
          <span class="bb-side__score">{{ homeScore() }}</span>
        </div>
        <div class="bb-clock">
          <div class="bb-clock__time" aria-live="polite">{{ clockDisplay() }}</div>
          <div class="bb-clock__period">{{ g.quarterCount === 4 ? 'Q' : 'H' }}{{ clock()?.currentQuarter ?? 1 }}</div>
          <div class="bb-clock__controls">
            <button mat-flat-button color="primary" type="button" (click)="toggleClock()">
              <mat-icon>{{ clock()?.running ? 'pause' : 'play_arrow' }}</mat-icon>
              {{ clock()?.running ? 'Pause' : 'Start' }}
            </button>
            <button mat-stroked-button type="button" (click)="resetQuarter()">
              <mat-icon>restart_alt</mat-icon>
              Reset
            </button>
            <button mat-stroked-button type="button" (click)="nextQuarter()">
              <mat-icon>skip_next</mat-icon>
              Next period
            </button>
          </div>
        </div>
        <div class="bb-side bb-side--away">
          <span class="bb-side__label">{{ g.opponent || 'Away' }}</span>
          <span class="bb-side__score">{{ awayScore() }}</span>
        </div>
      </section>
    }

    <div class="bb-actions">
      @if (sess.status === statusActive) {
        <button mat-stroked-button (click)="pause()" type="button">
          <mat-icon>pause</mat-icon>
          Pause game
        </button>
      } @else if (sess.status === statusPaused) {
        <button mat-stroked-button (click)="resume()" type="button">
          <mat-icon>play_arrow</mat-icon>
          Resume game
        </button>
      }
      <button mat-stroked-button (click)="undoLast()" type="button">
        <mat-icon>undo</mat-icon>
        Undo last play
      </button>
      <button mat-stroked-button (click)="shareLiveLink()" type="button">
        <mat-icon>share</mat-icon>
        Share live link
      </button>
      <button mat-flat-button color="warn" (click)="end()" type="button">
        <mat-icon>stop</mat-icon>
        End game
      </button>
    </div>

    <mat-tab-group class="brijjsports-card bb-tabs">
      <mat-tab label="Live">
        <div class="bb-live">
          <div class="bb-live__roster">
            <h3 class="bb-live__heading">On court</h3>
            <ul class="bb-roster">
              @for (a of onCourt(); track trackAthlete($index, a)) {
                <li class="bb-roster__row" [class.bb-roster__row--active]="selectedPlayer() === a.id">
                  <button mat-stroked-button class="bb-roster__select" type="button" (click)="selectPlayer(a.id)">
                    <span class="bb-avatar">{{ a.initials }}</span>
                    {{ a.name }}
                  </button>
                  <button mat-icon-button type="button" (click)="toggleSub(a.id)" aria-label="Sub out">
                    <mat-icon>swap_horiz</mat-icon>
                  </button>
                </li>
              } @empty {
                <li class="bb-roster__empty">No players on court — sub from bench.</li>
              }
            </ul>

            <h3 class="bb-live__heading">Bench</h3>
            <ul class="bb-roster bb-roster--bench">
              @for (a of bench(); track trackAthlete($index, a)) {
                <li class="bb-roster__row">
                  <span class="bb-roster__name">
                    <span class="bb-avatar bb-avatar--muted">{{ a.initials }}</span>
                    {{ a.name }}
                  </span>
                  <button mat-icon-button type="button" (click)="toggleSub(a.id)" aria-label="Sub in">
                    <mat-icon>arrow_upward</mat-icon>
                  </button>
                </li>
              } @empty {
                <li class="bb-roster__empty">Bench empty.</li>
              }
            </ul>
          </div>

          <div class="bb-live__pad">
            <p class="bb-live__hint">
              @if (selectedPlayer(); as sel) {
                Recording for
                <strong>{{ state.athleteById(sel)?.name }}</strong>
              } @else {
                Tap a player on court, then a stat below.
              }
            </p>
            <div class="bb-quick">
              @for (q of quickActions; track q.kind) {
                <button
                  mat-flat-button
                  type="button"
                  [color]="q.tone ?? null"
                  [disabled]="!selectedPlayer()"
                  (click)="recordHome(q.kind)"
                >
                  {{ q.label }}
                </button>
              }
            </div>

            <h3 class="bb-live__heading">Away team scoring</h3>
            <div class="bb-quick bb-quick--away">
              <button mat-stroked-button type="button" (click)="recordAway('2pt_made')">+2 Away</button>
              <button mat-stroked-button type="button" (click)="recordAway('3pt_made')">+3 Away</button>
              <button mat-stroked-button type="button" (click)="recordAway('ft_made')">FT Away</button>
            </div>
          </div>
        </div>
      </mat-tab>

      <mat-tab label="Plays">
        <ul class="bb-plays">
          @for (p of playLog(); track trackPlay($index, p)) {
            <li class="bb-plays__row" [class.bb-plays__row--away]="p.team === 'away'">
              <span class="bb-plays__period">{{ game()?.quarterCount === 4 ? 'Q' : 'H' }}{{ p.quarter }}</span>
              <span class="bb-plays__clock">{{ formatClockSec(p.clockSec) }}</span>
              <span class="bb-plays__player">{{ p.playerName }}</span>
              <span class="bb-plays__label">{{ p.label }}</span>
            </li>
          } @empty {
            <li class="bb-plays__empty">No plays yet.</li>
          }
        </ul>
      </mat-tab>

      <mat-tab label="Box score">
        <table mat-table [dataSource]="boxScoreRows()" class="bb-table" [trackBy]="trackBox">
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Player</th>
            <td mat-cell *matCellDef="let r">
              <span class="bb-avatar">{{ r.initials }}</span>
              {{ r.name }}
            </td>
          </ng-container>
          <ng-container matColumnDef="pts">
            <th mat-header-cell *matHeaderCellDef>PTS</th>
            <td mat-cell *matCellDef="let r">{{ r.points }}</td>
          </ng-container>
          <ng-container matColumnDef="fg">
            <th mat-header-cell *matHeaderCellDef>FG</th>
            <td mat-cell *matCellDef="let r">{{ r.fgMade }}/{{ r.fgAtt }}</td>
          </ng-container>
          <ng-container matColumnDef="3p">
            <th mat-header-cell *matHeaderCellDef>3P</th>
            <td mat-cell *matCellDef="let r">{{ r.threePtMade }}/{{ r.threePtAtt }}</td>
          </ng-container>
          <ng-container matColumnDef="ft">
            <th mat-header-cell *matHeaderCellDef>FT</th>
            <td mat-cell *matCellDef="let r">{{ r.ftMade }}/{{ r.ftAtt }}</td>
          </ng-container>
          <ng-container matColumnDef="reb">
            <th mat-header-cell *matHeaderCellDef>REB</th>
            <td mat-cell *matCellDef="let r">{{ r.rebTotal }}</td>
          </ng-container>
          <ng-container matColumnDef="ast">
            <th mat-header-cell *matHeaderCellDef>AST</th>
            <td mat-cell *matCellDef="let r">{{ r.ast }}</td>
          </ng-container>
          <ng-container matColumnDef="stl">
            <th mat-header-cell *matHeaderCellDef>STL</th>
            <td mat-cell *matCellDef="let r">{{ r.stl }}</td>
          </ng-container>
          <ng-container matColumnDef="blk">
            <th mat-header-cell *matHeaderCellDef>BLK</th>
            <td mat-cell *matCellDef="let r">{{ r.blk }}</td>
          </ng-container>
          <ng-container matColumnDef="pf">
            <th mat-header-cell *matHeaderCellDef>PF</th>
            <td mat-cell *matCellDef="let r">{{ r.pf }}</td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="boxColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: boxColumns"></tr>
        </table>
      </mat-tab>

      <mat-tab label="Per-quarter (manual)">
        <mat-tab-group class="bb-inner-tabs">
          @for (q of quarters(); track q) {
            <mat-tab [label]="(game()?.quarterCount === 4 ? 'Q' : 'H') + q + ' · ' + quarterScore(q) + ' pts'">
              <table mat-table [dataSource]="rowsForQuarter(q)" class="bb-table">
                <ng-container matColumnDef="name">
                  <th mat-header-cell *matHeaderCellDef>Player</th>
                  <td mat-cell *matCellDef="let r">
                    <span class="bb-avatar">{{ r.initials }}</span>
                    {{ r.name }}
                  </td>
                </ng-container>

                @for (key of statKeys; track key) {
                  <ng-container [matColumnDef]="key">
                    <th mat-header-cell *matHeaderCellDef>
                      {{ key === 'minutesPlayed' ? 'MIN' : key.charAt(0).toUpperCase() + key.slice(1, 3) }}
                    </th>
                    <td mat-cell *matCellDef="let r">
                      <mat-form-field appearance="outline" class="bb-stat">
                        <input
                          matInput
                          type="number"
                          min="0"
                          [value]="r.line[key]"
                          (change)="updateStat(r.athleteId, q, key, $event)"
                          [attr.aria-label]="key + ' for ' + r.name"
                        />
                      </mat-form-field>
                    </td>
                  </ng-container>
                }

                <ng-container matColumnDef="total">
                  <th mat-header-cell *matHeaderCellDef>Total pts</th>
                  <td mat-cell *matCellDef="let r">{{ r.totalPoints }}</td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
              </table>
            </mat-tab>
          }
        </mat-tab-group>
      </mat-tab>
    </mat-tab-group>
  } @else {
    <p class="brijjsports-empty">Game not found.</p>
  }
</div>
